import React, { useState, useRef, useEffect } from 'react';
import { Button, Input, Select, Message, Spin, Empty, Typography, Card, Row, Col, Space, TextArea } from '@douyinfe/semi-ui';
import { IconSend, IconPlus, IconDelete, IconEdit, IconDownload, IconImage, IconSearch } from '@douyinfe/semi-icons';
import { API, showError, showSuccess } from '../../helpers';
import MarkdownRenderer from '../../components/MarkdownRenderer/index.js';
import './Playground.css';

const { Title, Text } = Typography;

const Playground = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apiToken, setApiToken] = useState('');
  const [uploadedImages, setUploadedImages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [currentConvId, setCurrentConvId] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [initializing, setInitializing] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    initializePlayground();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const initializePlayground = async () => {
    try {
      setInitializing(true);
      await Promise.all([fetchModels(), loadApiToken(), loadConversations()]);
    } catch (err) {
      console.error('Failed to initialize playground:', err);
      showError('初始化失败，请刷新页面重试');
    } finally {
      setInitializing(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadApiToken = async () => {
    try {
      const res = await API.get('/api/user/token');
      if (res.data.success) {
        setApiToken(res.data.data);
      }
    } catch (error) {
      console.error('Failed to load API token:', error);
    }
  };

  const fetchModels = async () => {
    try {
      const res = await API.get('/api/user/available_models');
      if (res.data.success) {
        let modelList = res.data.data || [];
        if (Array.isArray(modelList) && modelList.length > 0) {
          setModels(modelList);
          if (!selectedModel) setSelectedModel(modelList[0]);
        } else {
          const fallbackRes = await API.get('/api/models');
          if (fallbackRes.data.success) {
            let fallbackModelList = fallbackRes.data.data || [];
            if (fallbackModelList && typeof fallbackModelList === 'object' && !Array.isArray(fallbackModelList)) {
              const modelSet = new Set();
              Object.values(fallbackModelList).forEach(models => {
                if (Array.isArray(models)) models.forEach(m => modelSet.add(m));
              });
              fallbackModelList = Array.from(modelSet).sort();
            }
            if (Array.isArray(fallbackModelList) && fallbackModelList.length > 0) {
              setModels(fallbackModelList);
              if (!selectedModel) setSelectedModel(fallbackModelList[0]);
            }
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch models:', error);
    }
  };

  const loadConversations = () => {
    try {
      const saved = localStorage.getItem('playground_conversations');
      if (saved) setConversations(JSON.parse(saved));
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  };

  const saveConversations = (convList) => {
    try {
      localStorage.setItem('playground_conversations', JSON.stringify(convList));
    } catch (error) {
      console.error('Failed to save conversations:', error);
    }
  };

  const newConversation = () => {
    if (currentConvId && messages.length > 0) {
      const updatedConvs = conversations.map(conv => {
        if (conv.id === currentConvId) {
          return { ...conv, messages, updatedAt: new Date().toISOString() };
        }
        return conv;
      });
      setConversations(updatedConvs);
      saveConversations(updatedConvs);
    }

    const newConv = {
      id: Date.now().toString(),
      title: '新对话 ' + new Date().toLocaleString(),
      messages: [],
      model: selectedModel,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    const updatedConvs = [newConv, ...conversations];
    setConversations(updatedConvs);
    saveConversations(updatedConvs);
    setCurrentConvId(newConv.id);
    setMessages([]);
    setInput('');
    setUploadedImages([]);
    showSuccess('已创建新对话');
  };

  const loadConversation = (convId) => {
    if (currentConvId && messages.length > 0) {
      saveConversations(conversations.map(conv => {
        if (conv.id === currentConvId) {
          return { ...conv, messages, updatedAt: new Date().toISOString() };
        }
        return conv;
      }));
    }

    const conv = conversations.find(c => c.id === convId);
    if (conv) {
      setCurrentConvId(convId);
      setMessages(conv.messages || []);
      setSelectedModel(conv.model || selectedModel);
    }
  };

  const deleteConversation = (convId, e) => {
    e.stopPropagation();
    const updatedConvs = conversations.filter(c => c.id !== convId);
    setConversations(updatedConvs);
    saveConversations(updatedConvs);
    if (currentConvId === convId) {
      setCurrentConvId(null);
      setMessages([]);
    }
    showSuccess('对话已删除');
  };

  const filteredConversations = conversations.filter(conv =>
    conv.title.toLowerCase().includes(searchText.toLowerCase())
  );

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + uploadedImages.length > 4) {
      showError('最多只能上传 4 张图片');
      return;
    }

    files.forEach(file => {
      if (!file.type.startsWith('image/')) {
        showError('只能上传图片文件');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showError('图片大小不能超过 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedImages(prev => [...prev, {
          file,
          preview: event.target.result,
          base64: event.target.result
        }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removeImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  const triggerFileUpload = () => {
    fileInputRef.current?.click();
  };

  const sendMessage = async () => {
    if (!input.trim() && uploadedImages.length === 0) return;
    if (!apiToken) {
      showError('请先获取 API Token');
      return;
    }
    if (models.length === 0 || !selectedModel) {
      showError('没有可用的模型，请先配置渠道');
      return;
    }

    let userMessageContent;
    if (uploadedImages.length > 0) {
      userMessageContent = [{ type: 'text', text: input.trim() || '请描述这张图片' }];
      uploadedImages.forEach(img => {
        userMessageContent.push({
          type: 'image_url',
          image_url: { url: img.base64, detail: 'high' }
        });
      });
    } else {
      userMessageContent = input.trim();
    }

    const userMessage = { role: 'user', content: userMessageContent };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setUploadedImages([]);
    setLoading(true);

    if (!currentConvId) {
      const newConv = {
        id: Date.now().toString(),
        title: input.trim().substring(0, 30) || '图片对话',
        messages: newMessages,
        model: selectedModel,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      const updatedConvs = [newConv, ...conversations];
      setConversations(updatedConvs);
      saveConversations(updatedConvs);
      setCurrentConvId(newConv.id);
    }

    try {
      const response = await fetch('/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiToken}`
        },
        body: JSON.stringify({
          model: selectedModel,
          messages: newMessages,
          stream: true
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantMessage = '';

      setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n').filter(line => line.trim());

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;

            try {
              const parsed = JSON.parse(data);
              const content = parsed.choices[0]?.delta?.content || '';
              assistantMessage += content;

              setMessages(prev => {
                const updated = [...prev];
                const lastMsg = updated[updated.length - 1];
                if (lastMsg.role === 'assistant') {
                  lastMsg.content = assistantMessage;
                }
                return updated;
              });
            } catch (e) {}
          }
        }
      }
    } catch (error) {
      showError('请求失败: ' + error.message);
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const clearConversation = () => {
    if (messages.length > 0 && !window.confirm('确定要清空当前对话吗?')) return;
    setMessages([]);
    setCurrentConvId(null);
    showSuccess('对话已清空');
  };

  const exportConversation = () => {
    if (messages.length === 0) {
      showError('没有可导出的对话');
      return;
    }
    const conv = conversations.find(c => c.id === currentConvId);
    const title = conv?.title || '对话导出';
    let markdown = `# ${title}\n\n导出时间: ${new Date().toLocaleString()}\n模型: ${selectedModel}\n\n---\n\n`;
    messages.forEach(msg => {
      const role = msg.role === 'user' ? '👤 用户' : '🤖 助手';
      markdown += `### ${role}\n\n${msg.content}\n\n---\n\n`;
    });
    const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title}_${new Date().getTime()}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    showSuccess('对话已导出');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (initializing) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        background: '#f5f7fa'
      }}>
        <Spin size="large" tip="正在加载在线体验..." />
      </div>
    );
  }

  if (!apiToken) {
    return (
      <div style={{ 
        padding: '24px', 
        background: '#f5f7fa', 
        minHeight: 'calc(100vh - 60px)' 
      }}>
        {/* 页面标题 */}
        <div style={{ 
          marginBottom: '24px',
          padding: '24px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '12px',
          color: '#fff'
        }}>
          <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '600' }}>
            💬 在线体验
          </h2>
          <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>
            与 AI 模型进行实时对话测试
          </p>
        </div>

        <Card style={{
          borderRadius: '8px',
          border: '1px solid #e4e7ed',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
        }}>
          <Empty
            title="需要 API Token"
            description="请先在令牌管理页面创建一个 API Token，然后才能使用在线体验功能"
            style={{ padding: '40px 20px' }}
          >
            <Button 
              theme="solid"
              onClick={() => window.location.href = '/token'}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                borderRadius: '8px',
                padding: '0 24px',
                height: '40px'
              }}
            >
              前往令牌管理
            </Button>
          </Empty>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', background: '#f5f7fa', minHeight: 'calc(100vh - 60px)' }}>
      {/* 页面标题区域 */}
      <div style={{ 
        marginBottom: '24px',
        padding: '24px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: '12px',
        color: '#fff',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h2 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: '600' }}>
            💬 在线体验
          </h2>
          <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>
            与 AI 模型进行实时对话，支持文本和多模态输入
          </p>
        </div>
        <Button 
          theme="borderless"
          onClick={newConversation}
          style={{
            background: 'rgba(255, 255, 255, 0.2)',
            color: '#fff',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '8px',
            backdropFilter: 'blur(10px)',
            height: '40px',
            padding: '0 20px'
          }}
        >
          <IconPlus /> 新对话
        </Button>
      </div>

      <div className="playground-container">
        {/* 左侧边栏 */}
        <div className="playground-sidebar">
          <Card style={{ 
            marginBottom: 16,
            borderRadius: '8px',
            border: '1px solid #e4e7ed',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
          }}>
            <Button 
              block 
              theme="solid" 
              onClick={newConversation}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                borderRadius: '8px',
                height: '40px'
              }}
            >
              <IconPlus /> 新建对话
            </Button>
          </Card>

        <Input
          prefix={<IconSearch />}
          placeholder="搜索对话..."
          value={searchText}
          onChange={setSearchText}
          style={{ 
            marginBottom: 16,
            borderRadius: '8px'
          }}
        />

        <div style={{ maxHeight: 300, overflowY: 'auto', marginBottom: 16 }}>
          {filteredConversations.length === 0 ? (
            <Empty description="暂无对话" style={{ padding: 20 }} />
          ) : (
            filteredConversations.map(conv => (
              <div
                key={conv.id}
                onClick={() => loadConversation(conv.id)}
                className={`conv-item ${currentConvId === conv.id ? 'active' : ''}`}
              >
                <div style={{ fontWeight: 'bold', marginBottom: 4, paddingRight: 48 }}>{conv.title}</div>
                <Text type="tertiary" size="small">{new Date(conv.updatedAt).toLocaleDateString()}</Text>
                <button className="conv-action" onClick={(e) => deleteConversation(conv.id, e)} title="删除">
                  <IconDelete />
                </button>
              </div>
            ))
          )}
        </div>

            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '16px' }}>🤖</span>
                选择模型
              </Text>
              {models.length === 0 ? (
                <Empty 
                  title="无可用模型"
                  description="请先在渠道管理中配置模型渠道"
                  style={{ padding: '20px 10px' }}
                >
                  <Button 
                    size="small"
                    onClick={() => window.location.href = '/channel'}
                    style={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      border: 'none',
                      borderRadius: '6px'
                    }}
                  >
                    前往配置
                  </Button>
                </Empty>
              ) : (
                <Select
                  value={selectedModel}
                  onChange={setSelectedModel}
                  style={{ 
                    width: '100%',
                    borderRadius: '8px'
                  }}
                  filter
                >
                  {models.map(m => (
                    <Select.Option key={m} value={m}>{m}</Select.Option>
                  ))}
                </Select>
              )}
            </div>

        <Card style={{
          borderRadius: '8px',
          border: '1px solid #e4e7ed',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
        }}>
          <Text strong style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <span style={{ fontSize: '16px' }}>📊</span>
            对话统计
          </Text>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ 
              padding: '10px',
              background: '#f5f7fa',
              borderRadius: '6px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Text type="tertiary" size="small">消息数</Text>
              <Text strong>{messages.length}</Text>
            </div>
            <div style={{ 
              padding: '10px',
              background: '#f5f7fa',
              borderRadius: '6px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Text type="tertiary" size="small">对话数</Text>
              <Text strong>{conversations.length}</Text>
            </div>
            <div style={{ 
              padding: '10px',
              background: '#f5f7fa',
              borderRadius: '6px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Text type="tertiary" size="small">状态</Text>
              <Text strong style={{ color: loading ? '#faad14' : '#52c41a' }}>
                {loading ? '⏳ 回复中...' : '✅ 就绪'}
              </Text>
            </div>
          </div>
        </Card>
      </div>

      {/* 主聊天区 */}
      <div className="playground-main">
        <Card style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '8px',
          border: '1px solid #e4e7ed',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
          overflow: 'hidden'
        }}>
          {messages.length > 0 && (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: '8px', 
              marginBottom: '12px',
              padding: '0 8px'
            }}>
              <Button 
                size="small" 
                onClick={exportConversation}
                style={{ borderRadius: '6px' }}
              >
                <IconDownload /> 导出
              </Button>
              <Button 
                size="small" 
                type="danger" 
                onClick={clearConversation}
                style={{ borderRadius: '6px' }}
              >
                <IconDelete /> 清空
              </Button>
            </div>
          )}
      
          <div className="messages-container">
            {messages.length === 0 ? (
              <div className="empty-state">
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <div style={{ fontSize: '64px', marginBottom: '16px' }}>💬</div>
                  <h3 style={{ 
                    margin: '0 0 8px 0', 
                    fontSize: '20px', 
                    fontWeight: '600',
                    color: '#303133'
                  }}>
                    开始新的对话
                  </h3>
                  <p style={{ 
                    margin: '0 0 24px 0', 
                    fontSize: '14px', 
                    color: '#909399',
                    lineHeight: '1.6'
                  }}>
                    选择一个模型，输入您的问题开始对话<br />
                    支持文本和多模态（图片）输入
                  </p>
                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'center',
                    flexWrap: 'wrap'
                  }}>
                    <div style={{
                      padding: '8px 16px',
                      background: '#f5f7fa',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: '#606266'
                    }}>
                      ✨ 支持 GPT-5
                    </div>
                    <div style={{
                      padding: '8px 16px',
                      background: '#f5f7fa',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: '#606266'
                    }}>
                      🎨 支持 Claude
                    </div>
                    <div style={{
                      padding: '8px 16px',
                      background: '#f5f7fa',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: '#606266'
                    }}>
                      🖼️ 支持图片输入
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              messages.map((msg, idx) => (
                <div key={idx} className={`message ${msg.role}`}>
                  <div className="message-avatar">
                    {msg.role === 'user' ? '👤' : '🤖'}
                  </div>
                  <div className="message-content">
                    <div className="message-bubble">
                      {msg.role === 'assistant' ? (
                        msg.content ? <MarkdownRenderer content={msg.content} /> : <Spin />
                      ) : Array.isArray(msg.content) ? (
                        <div>
                          {msg.content.map((item, i) => {
                            if (item.type === 'text') return <span key={i}>{item.text}</span>;
                            if (item.type === 'image_url') return <img key={i} src={item.image_url.url} alt="" style={{ maxWidth: 200, borderRadius: 8, marginTop: 8 }} />;
                            return null;
                          })}
                        </div>
                      ) : msg.content}
                    </div>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="input-area">
          {uploadedImages.length > 0 && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              {uploadedImages.map((img, idx) => (
                <div key={idx} style={{ position: 'relative', width: 80, height: 80 }}>
                  <img src={img.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }} />
                  <button onClick={() => removeImage(idx)} className="remove-img-btn">×</button>
                </div>
              ))}
            </div>
          )}

          <TextArea
            ref={inputRef}
            value={input}
            onChange={setInput}
            onKeyDown={handleKeyPress}
            placeholder="输入消息... (Shift+Enter 换行，Enter 发送)"
            disabled={loading}
            rows={3}
            style={{ borderRadius: 12 }}
          />

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
            <Space>
              <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleImageUpload} style={{ display: 'none' }} />
              <Button 
                onClick={triggerFileUpload} 
                disabled={loading || uploadedImages.length >= 4}
                style={{ 
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  fontWeight: 'bold',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                }}
              >
                <IconImage /> 上传图片
              </Button>
              {uploadedImages.length > 0 && <Text type="tertiary">已上传 {uploadedImages.length}/4</Text>}
            </Space>
            <Button theme="solid" onClick={sendMessage} loading={loading} disabled={!input.trim() && uploadedImages.length === 0}>
              <IconSend /> 发送
            </Button>
          </div>
        </div>
      </Card>
    </div>
  </div>
</div>
  );
};

export default Playground;
